import {
  ADMIN_OTP,
  AdminLogo,
  Button,
  Captcha,
  CaptchaSlot,
  Disclaimer,
  H3,
  H4,
  P,
  isAdmin,
  isBrandOwner,
  isPartner,
  isSales,
  getPendingReferralLocationId,
  getPhoneFromLS,
  removeUserDataFromLS,
  setPhoneNumberLS,
  signIn,
  signInCaptchaAction,
  signInV2,
  store,
  useAuth,
  useCaptcha,
  reportError,
} from '@vgrubs/components';
import { UserRole } from 'components/src/types/user.types';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useEffect, useRef, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { ErrorToast } from '../../adminComponents';

const AdminInput = styled.div`
  border: 1px solid ${({ theme }) => theme.darkGray}80;
  background: none;
  border-radius: 200px;
  padding: 0 20px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  width: 100%;
  text-align: center;

  input {
    background: none;
    text-align: center;
    border: none;
    :focus {
      outline: none;
    }
  }

  position: relative;

  .PhoneInputCountry {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translate(0, -50%);

    select {
      height: 35px;
    }

    .PhoneInputCountryIcon {
      height: 25px;
      width: 25px;
      border-radius: 50%;
      overflow: hidden;

      img {
        object-fit: cover;
      }

      border: none;
      box-shadow: none;
      background-color: none;
    }
    .PhoneInputCountrySelectArrow {
      display: none;
    }
  }

  :focus-visible {
    outline: ${({ theme }) => theme.primary} 1px;
  }
`;

const Form = styled.form`
  width: 100%;
`;

export const LoginAdmin = () => {
  const navigate = useNavigate();
  /*
   * Offered rather than typed again. It matters most on a phone: an installed
   * web app gets its own storage on iOS, so signing in there is a fresh start
   * even for someone already signed in in the browser, and this is the half
   * of that we can spare them.
   */
  const [phoneNumber, setPhoneNumber] = useState(() => getPhoneFromLS() ?? '');
  // Loading lives on the button (not a full-screen overlay) so the captcha
  // widget stays usable during verification.
  const [loading, setLoading] = useState(false);
  // True while an interactive captcha challenge is open — used to drop the
  // button spinner so the user isn't looking at a "loading" button while they
  // still have to solve the captcha.
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const { saveLoginPhoneNumber, saveLoginHasEmail, saveLoginIsNewUser } =
    useAuth();
  const captchaRef = useRef<HTMLDivElement>(null);
  const { getCaptchaToken } = useCaptcha(
    process.env.NX_TURNSTILE_SITE_KEY_SIGN_IN,
    signInCaptchaAction(),
    captchaRef,
    setCaptchaOpen
  );

  const role = (): UserRole => {
    if (isAdmin) return UserRole.admin;
    if (isSales) return UserRole.salesManager;
    if (isBrandOwner) return UserRole.brandOwner;
    return UserRole.staff;
  };

  /*
   * Merchant (v2) sends no role: the backend reads it off the number, and an
   * unknown number becomes a new owner account. The referral is sent whenever
   * there is one - the backend keeps it for a new account only.
   */
  async function requestCode(captcha: Captcha) {
    if (!isPartner) {
      const { data } = await signIn({ role: role(), phoneNumber, captcha });
      return { ...data.data, isNewUser: false };
    }

    const referralLocationId = getPendingReferralLocationId();
    const { data } = await signInV2({
      phoneNumber,
      captcha,
      ...(referralLocationId ? { referralLocationId } : {}),
    });
    return data.data;
  }

  async function handleSignIn(captcha: Captcha) {
    try {
      setLoading(true);
      const { isEmailAvailable, isNewUser } = await requestCode(captcha);

      saveLoginPhoneNumber(phoneNumber);
      // Remembered only once the backend has accepted it and sent the code,
      // so a typo never becomes the number this browser offers next time.
      setPhoneNumberLS(phoneNumber);
      saveLoginHasEmail(isEmailAvailable);
      saveLoginIsNewUser(isNewUser);
      navigate(ADMIN_OTP);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function handleSubmit() {
    if (!phoneNumber) return ErrorToast('Please enter your phone number');
    if (!isValidPhoneNumber(phoneNumber ?? ''))
      return ErrorToast('Check the phone number');

    // Button shows its spinner immediately, through the captcha verification.
    setLoading(true);
    const token = await getCaptchaToken();
    if (!token) {
      setLoading(false);
      return;
    }

    handleSignIn({ token });
  }

  useEffect(() => {
    store('redirected', true);

    if (isPartner) {
      removeUserDataFromLS();
      document.cookie = `token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.vgrubs.com;`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <AdminLogo />
      <H4 $m="24px">
        {isAdmin && 'Admin'}
        {isPartner && 'Merchant'}
        {isSales && 'Sales'}
        {isBrandOwner && 'Brands'}
      </H4>
      <H3 $mb="24">
        <span>Sign in</span>
      </H3>
      <P $m="20px 0 8px 0">
        <b>Your Phone Number</b>
      </P>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <AdminInput>
          <PhoneInput
            placeholder="Enter here"
            // Lets both platforms offer the number from the keyboard bar
            // instead of making someone recall it.
            autoComplete="tel"
            value={phoneNumber}
            onChange={(value) => {
              const v = value ? value.toString() : '';
              setPhoneNumber(v);
            }}
            defaultCountry="US"
            addInternationalOption={false}
            international
            focusInputOnCountrySelection={true}
          />
        </AdminInput>
        <CaptchaSlot ref={captchaRef} $activeMargin="12px 0 0" />

        <Button
          type="submit"
          $w="100%"
          $wSM="100%"
          $selected
          $mt="12"
          $loading={loading && !captchaOpen}
          disabled={loading}
        >
          Continue
        </Button>
      </Form>
      {isAdmin && (
        <Disclaimer $m="20px 0 0" style={{ whiteSpace: 'nowrap' }}>
          Admin access only. Enter your phone number to log in.
          <br /> Don’t have access yet? Ask your team lead to invite you.
        </Disclaimer>
      )}

      {isPartner && (
        <Disclaimer $m="20px 0 0">
          New here? Enter your phone number and we'll set up your restaurant
          account.
        </Disclaimer>
      )}
    </>
  );
};
